import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import { DateTime } from 'luxon'

import { RootState } from 'store'
import { EquipmentResponse } from 'store/equipments/equipments.types'
import { Order, OrderEvent } from 'store/orders/orders.types'
import {
  addEquipments,
  removeTaskById,
  removeTractor,
  removeTrailer,
  removeTrailerById,
  replaceTrailerById,
  selectOrderId,
} from 'store/trips/trips.slice'
import { TripPayload } from 'store/trips/trips.types'
import { useSessionOrderEventCleanup } from 'store/trips/use-session-order-event-cleanup'
import { formatToTitleCase } from 'utils/typography'

import { addTractor } from '../trip-tractors-list-old/trip-tractors-list-item/trip-tractors-list-item.utils'
import { addTrailer } from '../trip-trailers-list-old/trip-trailers-list-item/trip-trailers-list-item.utils'
import { addEquipment } from '../trip-selection-old/trip-orders-list/trip-orders-item/trip-order-item.utils'
import {
  isEventInTripTasks,
  isOrderLinkedDropTrailerEvent,
  isOrderLinkedHookTrailerEvent,
  isOrderLinkedHookTrailerTask,
  sortOrderEventsForDisplay,
  sortOrderEventsForTrip,
} from '../trip-selection-old/trip-orders-list/trip-order-event.utils'
import { isAvailableEvent } from '../trip-selection-old/trip-orders-list/trip-orders-list.utils'
import { useTripFormMode } from '../create-trip-context'
import {
  getTripOrderEventIds,
  TripBuilderSelectionMode,
} from './trip-builder-selection.types'

type TripBuilderSelectionPanelProps = {
  open: boolean
  mode: TripBuilderSelectionMode
  equipments: EquipmentResponse[]
  orders: Order[]
  replaceEquipment?: boolean
  replaceTrailerId?: string | null
  targetOrderTrailerId?: string | null
  isEditingOrders?: boolean
  onClose: () => void
}

const isOrderEventTask = (task: TripPayload) =>
  task.orderEventId != null &&
  (task.type === 'PICKUP' ||
    task.type === 'DELIVERY' ||
    isOrderLinkedHookTrailerTask(task) ||
    (task.type === 'DROP_TRAILER' && !!task.orderId))

/** Same orderEventId can appear as both DROP_TRAILER and HOOK_TRAILER. */
const getOrderEventKey = (event: {
  orderEventId?: number | null
  eventType: string
}) => `${event.orderEventId}:${event.eventType}`

const tripTaskToOrderEvent = (
  task: TripPayload,
  order: Order
): OrderEvent | null => {
  if (task.orderEventId == null) return null

  const fromOrder = [
    ...(order.orderEvents ?? []),
    ...(order.exceptions ?? []),
  ].find(
    (event) =>
      event.orderEventId === task.orderEventId && event.eventType === task.type
  )

  if (fromOrder) return fromOrder

  return {
    orderEventId: task.orderEventId,
    eventType: task.type,
    name: task.name,
    address: task.address,
    startTime: task.startTime,
    endTime: task.estimatedTime,
    status: 'ASSIGNED',
  } as OrderEvent
}

const TripBuilderSelectionPanel = ({
  open,
  mode,
  equipments,
  orders,
  replaceEquipment = false,
  replaceTrailerId = null,
  targetOrderTrailerId = null,
  isEditingOrders = false,
  onClose,
}: TripBuilderSelectionPanelProps) => {
  const dispatch = useDispatch()
  const formMode = useTripFormMode()
  const isEditMode = formMode === 'edit'
  const { deleteSessionOrderEvents } = useSessionOrderEventCleanup()
  const tripTasks = useSelector(
    (state: RootState) => state.trips.tripsPayload.tripTasks
  )
  const tripsPayload = useSelector(
    (state: RootState) => state.trips.tripsPayload
  )

  const [selectedEquipmentId, setSelectedEquipmentId] = useState<number | null>(
    null
  )
  const [selectedEventIds, setSelectedEventIds] = useState<number[]>([])

  useEffect(() => {
    if (!open) {
      setSelectedEquipmentId(null)
      setSelectedEventIds([])
      return
    }

    if (mode === 'tractor' && replaceEquipment) {
      const hookTractor = tripTasks.find((task) => task.type === 'HOOK_TRACTOR')
      setSelectedEquipmentId(
        hookTractor?.tractorId ? Number(hookTractor.tractorId) : null
      )
      return
    }

    if (mode === 'trailer' && replaceEquipment) {
      const hookTrailer = tripTasks.find(
        (task) =>
          task.type === 'HOOK_TRAILER' &&
          task.orderEventId == null &&
          task.sourceOrderPkId == null &&
          !task.orderId &&
          (replaceTrailerId == null || task.trailerId === replaceTrailerId)
      )
      setSelectedEquipmentId(
        hookTrailer?.trailerId ? Number(hookTrailer.trailerId) : null
      )
      return
    }

    if (mode === 'order' && isEditingOrders) {
      setSelectedEventIds(
        getTripOrderEventIds(
          tripTasks.filter(
            (task) =>
              targetOrderTrailerId == null ||
              task.trailerId === targetOrderTrailerId
          )
        )
      )
    }
  }, [
    open,
    mode,
    replaceEquipment,
    replaceTrailerId,
    targetOrderTrailerId,
    isEditingOrders,
    tripTasks,
  ])

  const tractors = useMemo(
    () => equipments.filter((item) => item.equipmentType === 'TRACTOR'),
    [equipments]
  )
  const trailers = useMemo(
    () => equipments.filter((item) => item.equipmentType === 'TRAILER'),
    [equipments]
  )

  const relevantTripTasks = useMemo(
    () =>
      targetOrderTrailerId == null
        ? tripTasks
        : tripTasks.filter((task) => task.trailerId === targetOrderTrailerId),
    [tripTasks, targetOrderTrailerId]
  )

  const getOrderEvents = useCallback(
    (order: Order) => {
      const merged = new Map<string, OrderEvent>()

      for (const event of [
        ...(order.orderEvents ?? []),
        ...(order.exceptions ?? []),
      ]) {
        if (
          event.orderEventId == null ||
          (event.status !== 'ASSIGNED' && !isAvailableEvent(event))
        ) {
          continue
        }
        // Keep DROP_TRAILER and HOOK_TRAILER separate even when they share an id
        merged.set(getOrderEventKey(event), event)
      }

      if (isEditingOrders) {
        for (const task of tripTasks) {
          if (
            !isOrderEventTask(task) ||
            (targetOrderTrailerId != null &&
              task.trailerId !== targetOrderTrailerId) ||
            (task.orderId !== order.userOrderId &&
              task.sourceOrderPkId !== order.orderId)
          ) {
            continue
          }

          const event = tripTaskToOrderEvent(task, order)
          if (event?.orderEventId != null) {
            merged.set(getOrderEventKey(event), event)
          }
        }
      }

      return sortOrderEventsForTrip(Array.from(merged.values()))
    },
    [isEditingOrders, targetOrderTrailerId, tripTasks]
  )

  // Assigned elsewhere (or already on this trip while creating) — show but don't allow select.
  // Events already on this trip stay selectable when editing order selection.
  const isEventLocked = useCallback(
    (event: OrderEvent) => {
      const onCurrentTrip = isEventInTripTasks(relevantTripTasks, event)

      if (isEditingOrders && onCurrentTrip) return false
      if (onCurrentTrip) return true
      return !isAvailableEvent(event)
    },
    [isEditingOrders, relevantTripTasks]
  )

  const isOrderOnCurrentTrip = useCallback(
    (order: Order) =>
      relevantTripTasks.some(
        (task) =>
          task.orderEventId != null &&
          (task.sourceOrderPkId === order.orderId ||
            task.orderId === order.userOrderId)
      ),
    [relevantTripTasks]
  )

  const availableOrders = useMemo(() => {
    const filtered = orders.filter((order) => {
      const events = getOrderEvents(order)
      if (!events.length) return false

      const hasSelectableEvent = events.some((event) => !isEventLocked(event))
      if (hasSelectableEvent) return true

      // Fully assigned / nothing selectable — hide, unless editing an order already on this trip
      if (isEditingOrders) {
        return isOrderOnCurrentTrip(order)
      }

      return false
    })

    // Change Pickup / edit: selected (on-trip) orders first, then the rest in place.
    if (isEditingOrders || isEditMode) {
      return [...filtered].sort((a, b) => {
        const aOnTrip = isOrderOnCurrentTrip(a)
        const bOnTrip = isOrderOnCurrentTrip(b)
        if (aOnTrip === bOnTrip) return 0
        return aOnTrip ? -1 : 1
      })
    }

    return filtered
  }, [
    orders,
    isEditMode,
    isEditingOrders,
    getOrderEvents,
    isEventLocked,
    isOrderOnCurrentTrip,
  ])

  const title = useMemo(() => {
    if (mode === 'tractor') return 'Select Tractor'
    if (mode === 'trailer') return 'Select Trailer'
    return isEditingOrders ? 'Change Pickup & Delivery' : 'Select Pickup & Delivery'
  }, [mode, isEditingOrders])

  const handleToggleEvent = (eventId: number, locked: boolean) => {
    if (locked) return

    setSelectedEventIds((prev) =>
      prev.includes(eventId)
        ? prev.filter((id) => id !== eventId)
        : [...prev, eventId]
    )
  }

  const handleSelectAllForOrder = (order: Order) => {
    const selectableIds = getOrderEvents(order)
      .filter(
        (event) => event.orderEventId != null && !isEventLocked(event)
      )
      .map((event) => event.orderEventId as number)

    if (!selectableIds.length) return

    const allSelected = selectableIds.every((id) =>
      selectedEventIds.includes(id)
    )
    if (allSelected) {
      setSelectedEventIds((prev) =>
        prev.filter((id) => !selectableIds.includes(id))
      )
      return
    }
    setSelectedEventIds((prev) =>
      Array.from(new Set(prev.concat(selectableIds)))
    )
  }

  const handleEquipmentSelect = (equipmentId: number, canSelect: boolean) => {
    if (!canSelect) return

    setSelectedEquipmentId((prev) => {
      if (replaceEquipment && prev === equipmentId) {
        return null
      }
      return equipmentId
    })
  }

  const handleConfirmEquipment = () => {
    if (!mode) return

    if (selectedEquipmentId == null) {
      if (mode === 'tractor' && replaceEquipment) {
        dispatch(removeTractor())
      }
      if (mode === 'trailer' && replaceEquipment) {
        if (replaceTrailerId != null) {
          dispatch(removeTrailerById({ trailerId: replaceTrailerId }))
        } else {
          dispatch(removeTrailer())
        }
      }
      onClose()
      return
    }

    const equipment = equipments.find(
      (item) => item.equipmentId === selectedEquipmentId
    )
    if (!equipment) return

    if (mode === 'tractor') {
      if (replaceEquipment || tripTasks.some((t) => t.type === 'HOOK_TRACTOR')) {
        dispatch(removeTractor())
      }
      dispatch(addEquipments(addTractor(equipment)))
    }

    if (mode === 'trailer') {
      const shouldReplace = replaceEquipment
      if (shouldReplace && replaceTrailerId != null) {
        dispatch(
          replaceTrailerById({
            trailerId: replaceTrailerId,
            equipment,
          })
        )
        onClose()
        return
      }

      const tasksForTrailer = shouldReplace
        ? tripTasks.filter(
            (t) =>
              t.orderEventId != null ||
              !!t.orderId ||
              (t.type !== 'HOOK_TRAILER' && t.type !== 'DROP_TRAILER')
          )
        : tripTasks
      if (shouldReplace) {
        dispatch(removeTrailer())
      }
      dispatch(addEquipments(addTrailer(tasksForTrailer, equipment)))
    }

    onClose()
  }

  const handleConfirmOrderEvents = async () => {
    if (isEditingOrders) {
      const tasksToRemove = tripsPayload.tripTasks.filter(
        (task) =>
          isOrderEventTask(task) &&
          (targetOrderTrailerId == null ||
            task.trailerId === targetOrderTrailerId) &&
          task.orderEventId != null &&
          !selectedEventIds.includes(task.orderEventId)
      )

      await deleteSessionOrderEvents(
        tasksToRemove
          .map((task) => task.orderEventId)
          .filter((id): id is number => id != null)
      )

      for (const task of tasksToRemove) {
        dispatch(
          removeTaskById({
            orderEventId: task.orderEventId as number,
            type: task.type,
          })
        )
      }

      let currentTasks = tripsPayload.tripTasks.filter(
        (task) =>
          !tasksToRemove.some(
            (removed) =>
              removed.orderEventId === task.orderEventId &&
              removed.type === task.type
          )
      )

      const newTasks: ReturnType<typeof addEquipment> = []
      let lastOrderId: number | null = null

      for (const order of availableOrders) {
        const eventsToAdd = getOrderEvents(order).filter(
          (event) =>
            event.orderEventId != null &&
            selectedEventIds.includes(event.orderEventId) &&
            !isEventLocked(event) &&
            !isEventInTripTasks(currentTasks, event)
        )

        for (const orderEvent of eventsToAdd) {
          const payload = addEquipment(
            orderEvent.eventType,
            { ...tripsPayload, tripTasks: currentTasks },
            orderEvent,
            order,
            undefined,
            targetOrderTrailerId
          )
          currentTasks = [...currentTasks, ...payload].sort(
            (a, b) => a.sequenceId - b.sequenceId
          )
          newTasks.push(...payload)
          lastOrderId = order.orderId
        }
      }

      if (newTasks.length > 0) {
        dispatch(addEquipments(newTasks))
        if (lastOrderId != null) {
          dispatch(selectOrderId({ orderId: lastOrderId }))
        }
      }

      onClose()
      return
    }

    if (selectedEventIds.length === 0) return

    let currentTasks = tripsPayload.tripTasks
    const newTasks: ReturnType<typeof addEquipment> = []
    let lastOrderId: number | null = null

    for (const order of availableOrders) {
      const eventsToAdd = getOrderEvents(order).filter(
        (event) =>
          event.orderEventId != null &&
          selectedEventIds.includes(event.orderEventId) &&
          !isEventLocked(event) &&
          !isEventInTripTasks(currentTasks, event)
      )

      for (const orderEvent of eventsToAdd) {
        const payload = addEquipment(
          orderEvent.eventType,
          { ...tripsPayload, tripTasks: currentTasks },
          orderEvent,
          order,
          undefined,
          targetOrderTrailerId
        )
        currentTasks = [...currentTasks, ...payload].sort(
          (a, b) => a.sequenceId - b.sequenceId
        )
        newTasks.push(...payload)
        lastOrderId = order.orderId
      }
    }

    if (newTasks.length > 0) {
      dispatch(addEquipments(newTasks))
      if (lastOrderId != null) {
        dispatch(selectOrderId({ orderId: lastOrderId }))
      }
    }

    onClose()
  }

  const canConfirm =
    mode === 'order'
      ? isEditingOrders || selectedEventIds.length > 0
      : replaceEquipment || selectedEquipmentId != null

  const handleConfirm = () => {
    if (mode === 'order') {
      handleConfirmOrderEvents()
      return
    }
    handleConfirmEquipment()
  }

  const getEventBgColor = (event: OrderEvent) => {
    if (event.eventType === 'PICKUP' || isOrderLinkedHookTrailerEvent(event)) {
      return 'primary.light'
    }
    if (
      event.eventType === 'DELIVERY' ||
      isOrderLinkedDropTrailerEvent(event)
    ) {
      return 'success.light'
    }
    return 'secondary.light'
  }

  const renderEquipmentList = (items: EquipmentResponse[], label: string) => (
    <Stack
      spacing={0}
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
    >
      {items.map((item, index) => {
        const isSelected = selectedEquipmentId === item.equipmentId
        const alreadyUsed = tripTasks.some(
          (task) =>
            (label === 'Tractor' &&
              !replaceEquipment &&
              task.tractorId === String(item.equipmentId) &&
              task.type === 'HOOK_TRACTOR') ||
            (label === 'Trailer' &&
              task.trailerId === String(item.equipmentId) &&
              task.type === 'HOOK_TRAILER' &&
              task.orderEventId == null &&
              task.sourceOrderPkId == null &&
              !task.orderId &&
              (!replaceEquipment || task.trailerId !== replaceTrailerId))
        )
        const canSelect = !alreadyUsed

        return (
          <Box key={item.equipmentId}>
            {index > 0 && <Divider />}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              onClick={() => handleEquipmentSelect(item.equipmentId, canSelect)}
              sx={{
                px: 2,
                py: 1.5,
                cursor: canSelect ? 'pointer' : 'default',
                opacity: canSelect ? 1 : 0.55,
                bgcolor: isSelected ? 'primary.light' : 'common.white',
                '&:hover': {
                  bgcolor: canSelect ? 'action.hover' : undefined,
                },
              }}
            >
              <Checkbox
                checked={isSelected}
                disabled={!canSelect}
                onClick={(event) => event.stopPropagation()}
                onChange={() => handleEquipmentSelect(item.equipmentId, canSelect)}
                sx={{ p: 0.5 }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography fontWeight={700}>
                  {item.unitNumber || label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {label}
                </Typography>
              </Box>
            </Stack>
          </Box>
        )
      })}
      {items.length === 0 && (
        <Typography sx={{ p: 2 }} color="text.secondary">
          No {label.toLowerCase()}s available.
        </Typography>
      )}
    </Stack>
  )

  const renderOrdersList = () => (
    <Stack spacing={2}>
      {availableOrders.map((order) => {
        const events = getOrderEvents(order)
        const displayEvents = sortOrderEventsForDisplay(events)
        const selectableIds = events
          .filter(
            (event) => event.orderEventId != null && !isEventLocked(event)
          )
          .map((event) => event.orderEventId as number)
        const allSelected =
          selectableIds.length > 0 &&
          selectableIds.every((id) => selectedEventIds.includes(id))

        if (!events.length) return null

        return (
          <Stack
            key={order.orderId}
            spacing={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 2, py: 1.5 }}
            >
              <Typography fontWeight={700}>
                {order.userOrderId}
                {order.customerName ? ` - ${order.customerName}` : ''}
              </Typography>
              {selectableIds.length > 0 && (
                <Button
                  onClick={() => handleSelectAllForOrder(order)}
                  sx={{ textTransform: 'none', fontWeight: 600, minWidth: 0 }}
                >
                  {allSelected ? 'Deselect All' : 'Select All'}
                </Button>
              )}
            </Stack>

            {displayEvents.map((event) => {
              const eventId = event.orderEventId
              if (eventId == null) return null
              const locked = isEventLocked(event)
              const checked = selectedEventIds.includes(eventId) || locked
              const dateLabel = event.startTime
                ? DateTime.fromISO(event.startTime).toFormat('LLL dd, yyyy')
                : ''
              const timeLabel = event.startTime
                ? DateTime.fromISO(event.startTime).toFormat('hh:mm a')
                : ''

              return (
                <Box key={`${eventId}-${event.eventType}`}>
                  <Divider />
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    onClick={() => handleToggleEvent(eventId, locked)}
                    sx={{
                      px: 2,
                      py: 1.5,
                      cursor: locked ? 'default' : 'pointer',
                      opacity: locked ? 0.55 : 1,
                      bgcolor: getEventBgColor(event),
                    }}
                  >
                    <Checkbox
                      checked={checked}
                      disabled={locked}
                      onClick={(clickEvent) => clickEvent.stopPropagation()}
                      onChange={() => handleToggleEvent(eventId, locked)}
                      sx={{ p: 0.5 }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={600}
                      >
                        {formatToTitleCase(event.eventType)}
                      </Typography>
                      <Typography>
                        {[event.name, event.address].filter(Boolean).join(', ')}
                      </Typography>
                    </Box>
                    <Stack
                      direction="row"
                      spacing={2}
                      sx={{ flexShrink: 0, textAlign: 'right' }}
                    >
                      {!!dateLabel && (
                        <Typography fontWeight={700}>{dateLabel}</Typography>
                      )}
                      {!!timeLabel && (
                        <Typography fontWeight={700}>{timeLabel}</Typography>
                      )}
                    </Stack>
                  </Stack>
                </Box>
              )
            })}
          </Stack>
        )
      })}

      {availableOrders.length === 0 && (
        <Typography sx={{ p: 2 }} color="text.secondary">
          No orders available.
        </Typography>
      )}
    </Stack>
  )

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '80vh',
        },
      }}
    >
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {(mode === 'tractor' || mode === 'trailer') && (
          <Stack
            direction="row"
            alignItems="center"
            sx={{
              px: 2.5,
              py: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography fontWeight={700} fontSize="1.05rem">
              {title}
            </Typography>
          </Stack>
        )}

        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {mode === 'tractor' && renderEquipmentList(tractors, 'Tractor')}
          {mode === 'trailer' && renderEquipmentList(trailers, 'Trailer')}
          {mode === 'order' && renderOrdersList()}
        </Box>

        <Box
          sx={{
            px: 2,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Button
            variant="contained"
            color="primary"
            disabled={!canConfirm}
            onClick={handleConfirm}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              borderRadius: 1.5,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            Confirm Selection
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default TripBuilderSelectionPanel
